import React, { useState } from 'react';
import { connect } from 'react-redux';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  CssBaseline,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { styled, useTheme } from '@mui/material/styles';
import ToastHandler from 'components/Toast-Handler/toast-handler';
import backgroundimg from './background.png';
import tmsImg from './tms-login-page.png';
import PageHeader from 'global/PageHeader';

import { resetPassword } from 'actions/login-action';
import { tokens } from 'theme/tokens';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import FTIcons from 'assets/icons/FTIcons';

const ResetPasswordPage = ({ resetUserPassword, toast, isProcessing }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // Move styles outside component using styled components
  const RootGrid = styled('div')(({ backgroundimg }) => ({
    minHeight: '100vh',
    width: '100vw',
    backgroundImage: `url('${backgroundimg}')`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }));

  const ContainerGrid = styled('div')(({ theme }) => ({
    background: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[3],
    width: '90%',
    maxWidth: 900,
    minHeight: 180,
    display: 'flex',
    flexDirection: 'row',
    overflow: 'hidden',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      width: '95%',
      minHeight: 'auto',
      boxShadow: theme.shadows[1],
    },
  }));
  
  const LeftSection = styled('div')(({ theme }) => ({
    flex: 1.75,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#fafafa',
    height: '100%', // Ensure it fills ContainerGrid height
    [theme.breakpoints.down('md')]: {
      minHeight: 180,
      maxHeight: 220,
      flex: 1,
    },
  }));

  const RightSection = styled('div')(({ theme }) => ({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: theme.spacing(4),
    minHeight: 400,
    background: '#fff',
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(2),
      minHeight: 320,
      flex: 1,
    },
  }));

  const StyledImage = styled('img')({
    width: '100%',
    height: '100%',
    objectFit: 'cover', // Fill the parent and crop if needed
  });

  const StyledAvatar = styled(Avatar)(() => ({
    margin: 'auto',
    backgroundColor: colors.secondary.main,
  }));

  const [showPassword, setShowPassword] = useState(false);
  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);

  const passwordChecklist = [
    { label: 'At least 8 characters', test: (val) => val.length >= 8 },
    { label: 'One uppercase letter', test: (val) => /[A-Z]/.test(val) },
    { label: 'One lowercase letter', test: (val) => /[a-z]/.test(val) },
    { label: 'One number', test: (val) => /[0-9]/.test(val) },
    { label: 'One special character', test: (val) => /[^A-Za-z0-9]/.test(val) },
  ];

  const formik = useFormik({
    initialValues: {
      token: '',
      password: '',
      confirmPassword: '',
      passwordResetTimestamp: '',
    },

    validationSchema: Yup.object().shape({
      password: Yup.string().required('Please enter your password')
        .matches(
          /^.*(?=.{8,})((?=.*[!@#$%^&*()\-_=+{};:,<.>]){1})(?=.*\d)((?=.*[a-z]){1})((?=.*[A-Z]){1}).*$/,
          'Password must contain at least 8 characters, one uppercase, one number and one special case character'
        ),
      confirmPassword: Yup.string().required('Please confirm your password')
        .when('password', {
          is: password => (password && password.length > 0 ? true : false),
          then: Yup.string().oneOf([Yup.ref('password')], 'Password doesn\'t match')
        }),
    }),

    onSubmit: values => {
      const trimmed_data = {};      
      Object.keys(values).forEach(key => trimmed_data[key] = typeof(values[key]) === 'string' ? values[key].trim() : values[key]);
      trimmed_data['resetToken'] = document.location.search.substring(7);
      trimmed_data['email'] = 'ravisher.dhillon@gmail.com';
      trimmed_data['passwordResetTimestamp'] = new Date().toJSON();
      resetUserPassword(trimmed_data);
    }
  });

  if (toast) {
    if (toast.type === 'SUCCESS') {
      alert('Password Reset. Please login using new credentials.');
    }
  }

  return (
    <RootGrid backgroundimg={backgroundimg}>
      <CssBaseline />
      <ContainerGrid>
        {/* Left Section: Image */}
        {!isMobile && (
          <LeftSection>
            <StyledImage src={tmsImg} alt='img' />
          </LeftSection>
        )}
        {/* Right Section: Login */}
        <RightSection>
          <PageHeader
            title={'Reset Password'}
            subTitle={'Reset your password.'}
          />          
          <StyledAvatar>
            <LockOutlinedIcon />
          </StyledAvatar>          

          <form onSubmit={formik.handleSubmit}>
            {isProcessing ? <CircularProgress style={{position: 'absolute', top: '50%', marginLeft: '50%' }}/>: null}
            {toast ? <ToastHandler component='ResetPassword'
              toast={toast}
              type={toast.type} /> : ''}
            <TextField
              error={Boolean(formik.touched.password && formik.errors.password)}
              fullWidth
              helperText={formik.touched.password && formik.errors.password}
              label='Password'
              name='password'
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              type='password'
              value={formik.values.password}
              variant='outlined'              
              slotProps={{
                input: {
                  endAdornment: <InputAdornment position="end">
                    <IconButton
                      onClick={togglePasswordVisibility}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
              }}              
            />
            <TextField
              error={Boolean(formik.touched.confirmPassword && formik.errors.confirmPassword)}
              fullWidth
              helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
              label='Confirm Password'
              margin='normal'
              name='confirmPassword'
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              type='password'
              value={formik.values.confirmPassword}
              variant='outlined'
              slotProps={{
                input: {
                  endAdornment: <InputAdornment position="end">
                    <IconButton
                      onClick={togglePasswordVisibility}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
              }}              
            />
            <Box>
              <Stack direction={'column'} spacing={1}>
                {passwordChecklist.map(({ label, test }) => (
                  <Box display={'flex'} gap={.5}>
                    <Typography>{test(formik.values.password) ?
                      <FTIcons.CheckCircleIcon width={20} height={20} fill={colors.success.main} /> :
                      <FTIcons.CrossCircleIcon width={20} height={20} fill={colors.error.main} />}
                    </Typography>
                    <Typography variant='body1'>{label}</Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
            <Box my={2}>
              <Button
                color='primary'
                fullWidth
                size='large'
                type='submit'
                variant='contained'
              >
                Reset Password
              </Button>
            </Box>
          </form>
         </RightSection>
      </ContainerGrid>
    </RootGrid>
  );
};

const mapStateToProps = state => {
  const {tenant, isProcessing, toast} = state.loginReducer;  
  return {
    tenant,
    isProcessing,
    toast
  };
};

const mapDispatchToProps = dispatch => {
  return {
    resetUserPassword: passwordRequest => dispatch(resetPassword(passwordRequest))
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ResetPasswordPage);